import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { installGoogleAnalytics, trackPageView } from "../../lib/analytics";
import { absoluteUrl, createJsonLd, defaultRobots, getSeoMeta } from "../../lib/seo";

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([name, value]) => {
    element.setAttribute(name, value);
  });
}

export function SeoManager() {
  const location = useLocation();
  const path = `${location.pathname}${location.search}`;

  useEffect(() => {
    installGoogleAnalytics();
  }, []);

  useEffect(() => {
    const meta = getSeoMeta(location.pathname);
    const canonicalUrl = absoluteUrl(meta.canonicalPath);
    const robots = meta.robots ?? defaultRobots;

    document.title = meta.title;
    upsertMeta('meta[name="description"]', { name: "description", content: meta.description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: meta.title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: meta.description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: meta.title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: meta.description });
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });

    const jsonLd = document.getElementById("vibegenie-json-ld") ?? document.createElement("script");
    jsonLd.id = "vibegenie-json-ld";
    jsonLd.setAttribute("type", "application/ld+json");
    jsonLd.textContent = JSON.stringify(createJsonLd(meta));
    if (!jsonLd.parentElement) document.head.appendChild(jsonLd);

    trackPageView(path, meta.title);
  }, [location.pathname, path]);

  return null;
}
